import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ActivityLevel, GoalType, Sex, WeightBearingLevel } from "@jilian/shared";
import { useAppStore } from "../state/useAppStore";
import { Button, Card, ProgressBar, Segmented } from "../components/ui";

const inputCls =
  "w-full min-h-[48px] px-4 rounded-elem bg-black/5 dark:bg-white/10 border border-tone-border text-tone-ink text-[15px] font-semibold focus:outline-none focus:ring-1 focus:ring-tone-accent";

const TOTAL_STEPS = 3;

interface Draft {
  name: string;
  sex: Sex;
  birthYear: number;
  heightCm: number;
  weightKg: number;
  goal: GoalType;
  activity: ActivityLevel;
  bodyPart: string;
  injuryType: string;
  injuryDate: string;
  weightBearing: WeightBearingLevel;
  forefoot: string;
  trainingYears: number;
  sessionMinutes: number;
  sequences: { code: string; on: boolean }[];
  reminderTime: string;
  notification: "granted" | "denied" | "unsupported" | "idle";
}

export function Onboarding() {
  const nav = useNavigate();
  const setOnboardingDone = useAppStore((s) => s.setOnboardingDone);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<Draft>({
    name: "极练",
    sex: "male",
    birthYear: 1999,
    heightCm: 185,
    weightKg: 86,
    goal: "muscle_gain_priority",
    activity: "sedentary",
    bodyPart: "右脚小脚趾",
    injuryType: "骨折",
    injuryDate: "2025-05-01",
    weightBearing: "partial_weight_bearing",
    forefoot: "limited",
    trainingYears: 5,
    sessionMinutes: 75,
    sequences: [
      { code: "A", on: true },
      { code: "B", on: true },
      { code: "C", on: true },
      { code: "D", on: true },
      { code: "E", on: false },
    ],
    reminderTime: "18:30",
    notification: "idle",
  });

  const patch = <K extends keyof Draft>(k: K, v: Draft[K]) => setForm((f) => ({ ...f, [k]: v }));

  const requestNotification = async () => {
    if (!("Notification" in window)) {
      patch("notification", "unsupported");
      return;
    }
    try {
      const res = await Notification.requestPermission();
      patch("notification", res === "granted" ? "granted" : "denied");
    } catch {
      patch("notification", "unsupported");
    }
  };

  const finish = () => {
    setOnboardingDone(true);
    nav("/", { replace: true });
  };

  const next = () => setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div className="min-h-screen mx-auto max-w-[480px] px-4 pt-safe-top pb-safe-bottom flex flex-col justify-between">
      {/* 顶部进度指示 */}
      <div className="py-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[12px] font-bold text-tone-soft font-caption">
            引导设置 · 第 {step + 1} 步 / 共 {TOTAL_STEPS} 步
          </span>
          {step > 0 ? (
            <button onClick={prev} className="text-tone-accent text-[12px] font-bold min-h-[36px] active:scale-95">
              返回上一步
            </button>
          ) : null}
        </div>
        <ProgressBar value={((step + 1) / TOTAL_STEPS) * 100} />
      </div>

      {/* 主体卡片 */}
      <div className="py-2">
        {step === 0 && (
          <Card className="p-5 space-y-4 border border-tone-border">
            <div>
              <div className="text-[11px] font-bold text-tone-accent uppercase font-caption">第 1 步 / 共 3 步</div>
              <h2 className="text-[20px] font-bold text-tone-ink tracking-tight mt-0.5 font-title">
                身体档案与训练目标
              </h2>
              <p className="text-[12px] text-tone-soft mt-1 font-caption">
                用于初始化基础代谢（BMR）、推荐进阶幅度与每日营养缺口。
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">昵称</div>
                <input className={inputCls} value={form.name} onChange={(e) => patch("name", e.target.value)} />
              </div>

              <div>
                <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">生理性别</div>
                <Segmented<Sex>
                  value={form.sex}
                  onChange={(v) => patch("sex", v)}
                  options={[
                    { value: "male", label: "男" },
                    { value: "female", label: "女" },
                  ]}
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">出生年份</div>
                  <input
                    type="number"
                    className={inputCls}
                    value={form.birthYear}
                    onChange={(e) => patch("birthYear", Number(e.target.value))}
                  />
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">身高 (cm)</div>
                  <input
                    type="number"
                    className={inputCls}
                    value={form.heightCm}
                    onChange={(e) => patch("heightCm", Number(e.target.value))}
                  />
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">体重 (kg)</div>
                  <input
                    type="number"
                    className={inputCls}
                    value={form.weightKg}
                    onChange={(e) => patch("weightKg", Number(e.target.value))}
                  />
                </div>
              </div>

              <div>
                <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">阶段主要目标</div>
                <Segmented<GoalType>
                  value={form.goal}
                  onChange={(v) => patch("goal", v)}
                  options={[
                    { value: "muscle_gain_priority", label: "增肌优先" },
                    { value: "fat_loss_priority", label: "减脂优先" },
                    { value: "maintain", label: "力量与维持" },
                  ]}
                />
              </div>
            </div>

            <Button className="w-full min-h-[50px] font-bold text-[15px]" onClick={next}>
              保存并进入伤情约束设置 →
            </Button>
          </Card>
        )}

        {step === 1 && (
          <Card className="p-5 space-y-4 border border-tone-border">
            <div>
              <div className="text-[11px] font-bold text-tone-accent uppercase font-caption">第 2 步 / 共 3 步</div>
              <h2 className="text-[20px] font-bold text-tone-ink tracking-tight mt-0.5 font-title">
                康复伤情与承重硬锁
              </h2>
              <p className="text-[12px] text-tone-soft mt-1 font-caption">
                规则引擎将据此严格过滤存在受力风险的动作（如站姿深蹲、硬拉等）。
              </p>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">伤处部位</div>
                  <input className={inputCls} value={form.bodyPart} onChange={(e) => patch("bodyPart", e.target.value)} />
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">伤情类型</div>
                  <input className={inputCls} value={form.injuryType} onChange={(e) => patch("injuryType", e.target.value)} />
                </div>
              </div>

              <div>
                <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">当前医生许可承重级别</div>
                <Segmented<WeightBearingLevel>
                  value={form.weightBearing}
                  onChange={(v) => patch("weightBearing", v)}
                  options={[
                    { value: "non_weight_bearing", label: "不可承重" },
                    { value: "partial_weight_bearing", label: "部分承重" },
                    { value: "full_weight_bearing", label: "完全承重" },
                  ]}
                />
              </div>

              <div>
                <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">前足受力限制</div>
                <Segmented
                  value={form.forefoot}
                  onChange={(v) => patch("forefoot", v)}
                  options={[
                    { value: "prohibited", label: "禁止受力" },
                    { value: "limited", label: "轻度限制" },
                    { value: "unrestricted", label: "无限制" },
                  ]}
                />
              </div>

              <div className="p-3 rounded-elem bg-tone-danger/10 border border-tone-danger/30 space-y-1">
                <div className="flex items-center gap-1 text-[12px] font-bold text-tone-danger">
                  <span>⚠️ 医疗边界告知：</span>
                </div>
                <p className="text-[11px] text-tone-ink font-body leading-relaxed">
                  本系统为记录与规则筛选辅助工具，不替代临床诊断与影像学复查。承重进阶必须遵循主治医生医嘱。
                </p>
              </div>
            </div>

            <Button className="w-full min-h-[50px] font-bold text-[15px]" onClick={next}>
              确认保护规则并继续 →
            </Button>
          </Card>
        )}

        {step === 2 && (
          <Card className="p-5 space-y-4 border border-tone-border">
            <div>
              <div className="text-[11px] font-bold text-tone-accent uppercase font-caption">第 3 步 / 共 3 步</div>
              <h2 className="text-[20px] font-bold text-tone-ink tracking-tight mt-0.5 font-title">
                训练序列与提醒偏好
              </h2>
              <p className="text-[12px] text-tone-soft mt-1 font-caption">
                为你定制上肢主导与坐卧保护分化训练日。
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">启用的训练序列</div>
                <div className="grid grid-cols-2 gap-2">
                  {form.sequences.map((seq) => (
                    <button
                      key={seq.code}
                      type="button"
                      onClick={() =>
                        patch(
                          "sequences",
                          form.sequences.map((s) => (s.code === seq.code ? { ...s, on: !s.on } : s)),
                        )
                      }
                      className={`min-h-[42px] px-3 rounded-elem border flex items-center justify-between text-xs font-bold transition-all active:scale-95 ${
                        seq.on
                          ? "bg-tone-accentSoft border-tone-accent text-tone-accent"
                          : "bg-black/5 dark:bg-white/5 border-tone-border text-tone-soft"
                      }`}
                    >
                      <span>序列 {seq.code}</span>
                      <span>{seq.on ? "✓" : "—"}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">每日固定提醒时间</div>
                <input
                  type="time"
                  className={inputCls}
                  value={form.reminderTime}
                  onChange={(e) => patch("reminderTime", e.target.value)}
                />
              </div>

              <div className="pt-1">
                <Button variant="secondary" className="w-full min-h-[44px] font-bold text-xs" onClick={requestNotification}>
                  {form.notification === "granted"
                    ? "✓ 系统通知权限已开启"
                    : "🔔 申请浏览器训练通知权限"}
                </Button>
              </div>
            </div>

            <Button className="w-full min-h-[52px] font-bold text-[16px] shadow-sm" onClick={finish}>
              完成设置 · 开启极练之旅
            </Button>
          </Card>
        )}
      </div>

      <div className="py-2 text-center text-[11px] text-tone-soft font-caption">
        数据完全保存在本地设备（IndexedDB），支持离线使用与随时导出。
      </div>
    </div>
  );
}
